import React, { useState, useEffect, useMemo } from "react";
import {
  collection,
  query,
  onSnapshot,
  updateDoc,
  doc,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Card, CardHeader, CardContent, CardTitle } from "../components/Card";

const ITEMS_PER_PAGE = parseInt(process.env.REACT_APP_ITEMS_PER_PAGE, 10) || 20;

export default function UsersAdmin() {
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all"); // all, nounou, parent

  useEffect(() => {
    const q = query(
      collection(db, "users"),
      orderBy("createdAt", "desc"),
      limit(1000)
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const items = [];
      querySnapshot.forEach((d) => items.push({ id: d.id, ...d.data() }));
      setAllUsers(items);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const st = searchTerm.toLowerCase();
    let filtered = allUsers;

    // Filtrer par type
    if (filterType === "nounou") {
      filtered = filtered.filter((u) => u.isNounou === true);
    } else if (filterType === "parent") {
      filtered = filtered.filter((u) => !u.isNounou);
    }

    // Filtrer par recherche
    if (st) {
      filtered = filtered.filter(
        (u) =>
          (u.firstname && u.firstname.toLowerCase().includes(st)) ||
          (u.lastname && u.lastname.toLowerCase().includes(st)) ||
          (u.email && u.email.toLowerCase().includes(st)) ||
          (u.city && u.city.toLowerCase().includes(st)) ||
          (u.department && u.department.toLowerCase().includes(st))
      );
    }

    setFilteredUsers(filtered);
    setPage(1);
  }, [searchTerm, allUsers, filterType]);

  const pageCount = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const currentUsers = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredUsers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredUsers, page]);

  const updateUserLocal = (id, changes) => {
    setAllUsers((old) =>
      old.map((u) => (u.id === id ? { ...u, ...changes } : u))
    );
    setFilteredUsers((old) =>
      old.map((u) => (u.id === id ? { ...u, ...changes } : u))
    );
  };

  const toggleActive = async (user) => {
    const userRef = doc(db, "users", user.id);
    await updateDoc(userRef, { isActive: !user.isActive });
    updateUserLocal(user.id, { isActive: !user.isActive });
  };

  const toggleEmailVerified = async (user) => {
    const userRef = doc(db, "users", user.id);
    await updateDoc(userRef, { emailIsVerified: !user.emailIsVerified });
    updateUserLocal(user.id, { emailIsVerified: !user.emailIsVerified });
  };

  const toggleAccountVerified = async (user) => {
    // Contrôle si pièces sont présentes avant toggle
    if (!user.isVerified && (!(user.documentFrontUrl || user.pieceRecto) || !(user.documentBackUrl || user.pieceVerso))) {
      alert("Impossible de vérifier le compte: pièces d'identité manquantes (recto et verso requis).");
      return;
    }
    const userRef = doc(db, "users", user.id);
    await updateDoc(userRef, { isVerified: !user.isVerified });
    updateUserLocal(user.id, { isVerified: !user.isVerified });
  };

  const formatTimestamp = (ts) =>
    ts && ts.toDate ? moment(ts.toDate()).format("DD/MM/YYYY HH:mm") : "-";

  const stats = {
    total: allUsers.length,
    nounous: allUsers.filter((u) => u.isNounou).length,
    parents: allUsers.filter((u) => !u.isNounou).length,
    verified: allUsers.filter((u) => u.emailIsVerified).length,
  };

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-6">
          {/* Info Card */}
          <Card className="bg-gradient-to-r from-pink-50 to-rose-50 border-pink-200">
            <CardContent className="flex items-start gap-4">
              <div className="p-3 bg-[#ff9be4] rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 text-white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Gestion des utilisateurs
                </h3>
                <p className="text-sm text-gray-600">
                  Consultez et gérez tous les utilisateurs de la plateforme.{" "}
                  {stats.total} utilisateurs au total.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card
              className={`cursor-pointer transition-all ${
                filterType === "all"
                  ? "ring-2 ring-[#ff9be4] bg-pink-50"
                  : "hover:shadow-md"
              }`}
              onClick={() => setFilterType("all")}
            >
              <CardContent className="text-center">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-3xl font-bold text-[#ff9be4] mt-1">
                  {stats.total}
                </p>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all ${
                filterType === "nounou"
                  ? "ring-2 ring-purple-400 bg-purple-50"
                  : "hover:shadow-md"
              }`}
              onClick={() => setFilterType("nounou")}
            >
              <CardContent className="text-center">
                <p className="text-sm font-medium text-gray-600">Nounous</p>
                <p className="text-3xl font-bold text-purple-600 mt-1">
                  {stats.nounous}
                </p>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all ${
                filterType === "parent"
                  ? "ring-2 ring-blue-400 bg-blue-50"
                  : "hover:shadow-md"
              }`}
              onClick={() => setFilterType("parent")}
            >
              <CardContent className="text-center">
                <p className="text-sm font-medium text-gray-600">Parents</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">
                  {stats.parents}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="text-center">
                <p className="text-sm font-medium text-gray-600">Vérifiés</p>
                <p className="text-3xl font-bold text-green-600 mt-1">
                  {stats.verified}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Search and Table Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle>
                  {filterType === "all"
                    ? "Tous les utilisateurs"
                    : filterType === "nounou"
                    ? "Nounous"
                    : "Parents"}
                </CardTitle>
                <div className="relative flex-1 max-w-md">
                  <input
                    type="text"
                    placeholder="Rechercher par nom, email, ville..."
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pl-10 focus:outline-none focus:ring-2 focus:ring-[#ff9be4] focus:border-transparent"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoComplete="off"
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5 text-gray-400 absolute left-3 top-3"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                    />
                  </svg>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {currentUsers.length === 0 ? (
                <div className="text-center py-12">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-16 h-16 text-gray-300 mx-auto mb-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                    />
                  </svg>
                  <p className="text-gray-600 font-medium mb-2">
                    Aucun utilisateur trouvé
                  </p>
                  <p className="text-sm text-gray-500">
                    Essayez de modifier vos filtres ou votre recherche
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Utilisateur
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Localisation
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Dates
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentUsers.map((user) => (
                        <tr
                          key={user.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              {user.photo || user.profilPhotoUrl ? (
                                <img
                                  src={user.photo || user.profilPhotoUrl}
                                  alt="Profil"
                                  className="w-12 h-12 rounded-full object-cover ring-2 ring-gray-200"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gradient-to-br from-[#ff9be4] to-[#ff6bd4] rounded-full flex items-center justify-center text-white font-semibold">
                                  {user.firstname?.[0]?.toUpperCase() || "?"}
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {user.firstname} {user.lastname}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {user.email}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <p className="text-gray-900">
                                {user.phoneNumber || "-"}
                              </p>
                              <p className="text-gray-500 text-xs">
                                Né(e) le {user.birthdate || "-"}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <p className="text-gray-900">
                                {user.city || "-"}
                              </p>
                              <p className="text-gray-500 text-xs">
                                {user.postalCode && user.department
                                  ? `${user.postalCode} - ${user.department}`
                                  : user.postalCode || user.department || "-"}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              {user.isNounou ? (
                                <>
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                    Nounou
                                  </span>
                                  {user.type && (
                                    <span className="text-xs text-gray-500">
                                      {user.type}
                                    </span>
                                  )}
                                  {user.pricePerHour && (
                                    <span className="text-xs font-semibold text-[#ff9be4]">
                                      {user.pricePerHour} €/h
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                  Parent
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              {/* Email vérifié */}
                              {user.emailIsVerified ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium border border-green-200">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    className="w-3 h-3"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  Email vérifié
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium border border-gray-200">
                                  Email non vérifié
                                </span>
                              )}

                              {/* Compte vérifié */}
                              {user.isVerified ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium border border-purple-200">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    className="w-3 h-3"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.403 12.652a3 3 0 0 0 0-5.304 3 3 0 0 0-3.75-3.751 3 3 0 0 0-5.305 0 3 3 0 0 0-3.751 3.75 3 3 0 0 0 0 5.305 3 3 0 0 0 3.75 3.751 3 3 0 0 0 5.305 0 3 3 0 0 0 3.751-3.75Zm-2.546-4.46a.75.75 0 0 0-1.214-.883l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  Compte vérifié
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium border border-gray-200">
                                  Compte non vérifié
                                </span>
                              )}

                              {/* Compte actif/inactif */}
                              {user.isActive === false ? (
                                <span className="inline-flex items-center px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium border border-red-200">
                                  Compte inactif
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium border border-green-200">
                                  Compte actif
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-xs text-gray-500">
                              <p>Créé: {formatTimestamp(user.createdAt)}</p>
                              <p>Connexion: {formatTimestamp(user.lastLogin)}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-2">
                              <button
                                onClick={() => navigate(`/users/${user.id}`)}
                                className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-[#ff9be4] text-white text-xs font-medium hover:bg-[#ff6bd4] transition-colors"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  className="w-3 h-3"
                                >
                                  <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                                  <path
                                    fillRule="evenodd"
                                    d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                Voir
                              </button>
                              <button
                                onClick={() => toggleActive(user)}
                                className={`inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-medium transition-colors ${
                                  user.isActive
                                    ? "bg-red-500 hover:bg-red-600"
                                    : "bg-green-500 hover:bg-green-600"
                                }`}
                              >
                                {user.isActive ? "Désactiver compte" : "Activer compte"}
                              </button>
                              <button
                                onClick={() => toggleEmailVerified(user)}
                                className={`inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-medium transition-colors ${
                                  user.emailIsVerified
                                    ? "bg-orange-500 hover:bg-orange-600"
                                    : "bg-green-600 hover:bg-green-700"
                                }`}
                              >
                                {user.emailIsVerified
                                  ? "Retirer vérif. email"
                                  : "Vérifier email"}
                              </button>
                              <button
                                onClick={() => toggleAccountVerified(user)}
                                className={`inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-medium transition-colors ${
                                  user.isVerified
                                    ? "bg-orange-500 hover:bg-orange-600"
                                    : "bg-purple-600 hover:bg-purple-700"
                                }`}
                              >
                                {user.isVerified
                                  ? "Retirer vérif. compte"
                                  : "Vérifier compte"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {pageCount > 1 && (
                <div className="flex justify-center items-center mt-6 gap-4">
                  <button
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    disabled={page === 1}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-4 h-4"
                    >
                      <path
                        fillRule="evenodd"
                        d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Précédent
                  </button>
                  <span className="text-sm font-medium text-gray-700">
                    Page {page} sur {pageCount}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(p + 1, pageCount))}
                    disabled={page === pageCount}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Suivant
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-4 h-4"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      }
    />
  );
}